import { Button, Card, Collapse, Form, Grid, message, Space } from 'antd';
import { Typography } from 'antd/lib';
import { ProjectOption } from 'osep-generator-service-client-v1';
import { Page } from 'osep-react-antd';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import packageJson from '../../package.json';
import { CliData } from '../cliDataSchema';
import InlineMarkdown from '../components/InlineMarkdown';
import GitLabProject from '../components/inputs/GitLabProject/GitLabProject';
import { Option as PathSegment } from '../components/inputs/GitLabProject/useOptions';
import FeatureGroup, {
  FeatureGroupType,
} from '../components/params/FeatureGroup';
import useCreateProductsMutation from '../hooks/mutations/useCreateProjectsMutation';
import useGenerateProjectMutation from '../hooks/mutations/useGenerateProjectMutation';
import useCli from '../hooks/query/useCli';
import { useGitLabMeQuery } from '../hooks/query/useGitLabMeQuery';
import {
  DEFAULT_FEATURE_GROUP,
  DEFAULT_PARAM_GROUP,
  RECOMMENDED_FEATURE_GROUP,
} from '../utils/consts';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import ConnectToGitLabPage from './ConnectToGitLabPage';
import ErrorPage from './ErrorPage';
import config from '../config';
import { useMsal } from '@azure/msal-react';

type FeatureGroups = { [featureGroup: string]: FeatureGroupType };

const ConfigureProjectPage: React.FC = () => {
  const [form] = Form.useForm();
  const { accounts } = useMsal();
  const { createProduct } = useCreateProductsMutation();
  const { generateProject } = useGenerateProjectMutation();
  const { projectName } = useParams<{ projectName: string }>();
  const navigate = useNavigate();
  const [creatingProject, setCreatingProject] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const {
    data: gitLabIdentity,
    isLoading: isGitLabLoading,
    isError: isGitLabError,
    error: gitLabError,
  } = useGitLabMeQuery();

  const {
    data: cliData,
    isLoading: isCliLoading,
    isError: isCliError,
    error: cliError,
  } = useCli();

  if (isCliError) {
    return <ErrorPage error={cliError} />;
  }

  if (isGitLabError) {
    return <ErrorPage error={gitLabError} />;
  }

  const isLoading = isGitLabLoading || isCliLoading;

  if (gitLabIdentity?.status === 404) {
    return <ConnectToGitLabPage />;
  }

  const cliDataTyped = cliData as CliData | undefined;

  const createCommand = cliDataTyped?.commands?.find(
    (cmd) => cmd.name === 'create',
  );
  const command = createCommand?.commands?.find(
    (cmd) => cmd.name === projectName,
  );

  function createOptions(values: any) {
    return visibleParams?.reduce((result, param) => {
      const value = values[param.name];
      const option = param.opts[0];

      if (param.name === 'osep_pipeline_variables') {
        const formattedVariables = values['osep_pipeline_variables'] || [];
        if (
          Array.isArray(formattedVariables) &&
          formattedVariables.length > 0
        ) {
          formattedVariables.forEach((formattedValue: string) => {
            result.push({ key: option, value: formattedValue });
          });
        }
        return result;
      }

      if (value) {
        if (param.is_flag && value) {
          result.push({
            key: option,
            value: undefined,
          });
        } else if (param.multiple) {
          let choices = values[param.name] ?? [];

          if (!Array.isArray(choices)) {
            choices = [choices];
          }

          result.push({ key: option, value: choices });
        } else {
          result.push({ key: option, value: value.toString() });
        }
      }

      return result;
    }, [] as Array<ProjectOption>);
  }

  const handleSubmit = async (values: any) => {
    if (values.confluence_space_and_parent_page) {
      if (values.confluence_space_and_parent_page.length !== 0) {
        values.confluence_space =
          values.confluence_space_and_parent_page[0].key;
        values.confluence_parent =
          values.confluence_space_and_parent_page[
            values.confluence_space_and_parent_page.length - 1
          ].key;
      }
    }

    const gitlab_project_path: PathSegment[] = values.gitlab_group;

    const gitlabParentGroupId =
      gitlab_project_path
        .slice()
        .reverse()
        .find((o) => o.type === 'group')?.value ?? '-1';

    const gitlabGroupFullPath = gitlab_project_path
      .map((o) => o.label)
      .join('/');

    const gitlabProjectPath = values.gitlab_project;

    const options = createOptions(values);

    try {
      setCreatingProject(true);

      if (options) {
        await createProduct({
          options,
          cliVersion: cliDataTyped?.version ?? '',
          projectType: command?.name ?? '',
          gitlabProjectPath,
          userEmail: accounts[0]?.username ?? '',
          gitlabGroupFullPath,
          gitlabParentGroupId: parseInt(gitlabParentGroupId, 10),
          gitlabPipelineRef: config.gitlabPipelineRef,
        }).then((project) => generateProject(project.id));
      }
      message.success('Project created successfully');
    } catch (error) {
      message.error('Error creating new project');
      console.error('Error creating new project:', error);
      return;
    } finally {
      setCreatingProject(false);
    }

    navigate('/projects');
  };

  const visibleParams =
    command?.params.filter(
      (param) => !param.hidden && !param.osep_generator_ui_hidden,
    ) ?? [];

  const featureGroups = visibleParams?.reduce<FeatureGroups>(
    (featureGroups, param) => {
      const featureGroup = param.osep_generator_ui_recommended
        ? RECOMMENDED_FEATURE_GROUP
        : param.osep_generator_ui_feature_group || DEFAULT_FEATURE_GROUP;

      const paramGroup =
        param.osep_generator_ui_param_group || DEFAULT_PARAM_GROUP;

      const existingFeatureGroupKey = Object.keys(featureGroups).find(
        (key) =>
          featureGroups[key][paramGroup] &&
          ((key === DEFAULT_FEATURE_GROUP &&
            featureGroup === RECOMMENDED_FEATURE_GROUP) ||
            (key === RECOMMENDED_FEATURE_GROUP &&
              featureGroup === DEFAULT_FEATURE_GROUP)),
      );

      const targetFeatureGroup = existingFeatureGroupKey || featureGroup;

      return {
        ...featureGroups,
        [targetFeatureGroup]: {
          ...featureGroups[targetFeatureGroup],
          [paramGroup]: [
            ...((featureGroups[targetFeatureGroup] ?? [])[paramGroup] ?? []),
            param,
          ],
        },
      };
    },
    { [DEFAULT_FEATURE_GROUP]: { [DEFAULT_PARAM_GROUP]: [] } },
  );

  if (featureGroups[DEFAULT_FEATURE_GROUP]) {
    Object.keys(featureGroups[DEFAULT_FEATURE_GROUP]).forEach((paramGroup) => {
      const params = featureGroups[DEFAULT_FEATURE_GROUP][paramGroup];
      if (params && params.length > 0) {
        featureGroups[DEFAULT_FEATURE_GROUP][paramGroup] = params.sort(
          (a, b) => {
            if (a.required && !b.required) return -1;
            if (!a.required && b.required) return 1;

            if (
              a.osep_generator_ui_recommended &&
              !b.osep_generator_ui_recommended
            )
              return -1;
            if (
              !a.osep_generator_ui_recommended &&
              b.osep_generator_ui_recommended
            )
              return 1;

            return (a.osep_generator_ui_display_name || a.name).localeCompare(
              b.osep_generator_ui_display_name || b.name,
            );
          },
        );
      }
    });
  }

  const defaultValues = {
    ...visibleParams?.reduce((defaultValues, param) => {
      if (param.default && param.show_default) {
        return { ...defaultValues, [param.name]: param.default.value };
      }

      return defaultValues;
    }, {}),
  };

  return (
    <Page
      title="Configure Project"
      onBack={() => navigate('/projects/add')}
      isLoading={isLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
    >
      <Space direction="vertical" size="large" style={{ display: 'flex' }}>
        <Card>
          <Card.Meta
            title={command?.osep_generator_ui_project_name}
            description={
              <InlineMarkdown>
                {command?.osep_generator_ui_help ?? ''}
              </InlineMarkdown>
            }
            avatar={
              <img
                src={findImageByProjectNameOrType(
                  command?.osep_generator_ui_project_group ?? '',
                )}
                height={80}
                alt=""
              />
            }
          ></Card.Meta>
        </Card>

        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={defaultValues}
          validateTrigger={['onSubmit', 'onChange']}
        >
          <Space direction="vertical" size="large" style={{ display: 'flex' }}>
            <Card title={'GitLab Project'}>
              <GitLabProject />
            </Card>

            {featureGroups && (
              <>
                {Object.keys(featureGroups).map((featureGroupKey) => {
                  if (
                    featureGroupKey !== DEFAULT_FEATURE_GROUP &&
                    featureGroupKey !== RECOMMENDED_FEATURE_GROUP
                  ) {
                    return null;
                  }
                  return (
                    <FeatureGroup
                      key={featureGroupKey}
                      featureGroup={featureGroups[featureGroupKey]}
                      visibleParams={visibleParams}
                      form={form}
                      command={command}
                    />
                  );
                })}
              </>
            )}
            <Collapse
              size="large"
              accordion={true}
              items={Object.keys(featureGroups ?? {})
                .filter(
                  (featureGroup) =>
                    featureGroup !== DEFAULT_FEATURE_GROUP &&
                    featureGroup !== RECOMMENDED_FEATURE_GROUP,
                )
                .map((featureGroup) => ({
                  key: featureGroup,
                  label: featureGroup,
                  children: (
                    <>
                      {featureGroups && (
                        <FeatureGroup
                          featureGroup={featureGroups[featureGroup]}
                          visibleParams={visibleParams}
                          form={form}
                          command={command}
                        />
                      )}
                    </>
                  ),
                }))}
            />
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={creatingProject}
                style={{ width: !screens.md ? '100%' : 'auto' }}
              >
                Create Project
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </Space>
    </Page>
  );
};

export default ConfigureProjectPage;
